/**
 * Help-content parsing (OPSFL-HELP-1, lane 1a) — a small hand-rolled parser for the contract
 * markdown format (docs/design/OPSFL-HELP-1-contract.md §Content format). NO new npm deps.
 * Pure functions: the loader feeds it raw strings; the coverage gate asserts `problems` is empty.
 * Parsing is TOLERANT (a malformed doc still yields whatever it can, with problems recorded) so a
 * bad content file degrades the panel, never crashes it.
 */

export interface SurfaceFieldHelp {
  /** The field label as authored between `**…**` (matched case-insensitively by InfoTip). */
  label: string;
  /** Plain-markdown meaning/units/constraints (inline markdown only). */
  body: string;
}

export interface SurfaceDoc {
  surfaceId: string;
  title: string;
  oneLiner: string;
  /** `## Purpose` block — plain markdown (paragraphs, optional bullets). */
  purpose: string;
  /** `## What you can do here` block — rendered as markdown at full fidelity. */
  actionsMd: string;
  /** `## Inputs & fields` block — rendered as markdown at full fidelity. */
  fieldsMd: string;
  /**
   * The strict `- **<Field label>** — <meaning>` entries extracted from `fieldsMd` — the InfoTip
   * lookup table. Richer content in the section (group headers, prose) renders in the panel but is
   * not field-addressable.
   */
  fields: SurfaceFieldHelp[];
  /**
   * Sections beyond the contract's four (rendered after Inputs & fields, in authored order) —
   * tolerated so authored depth is never silently dropped; the required four stay enforced.
   */
  extraSections: { heading: string; content: string }[];
  /** `## Terms on this page` — glossary term ids (kebab-case). */
  termIds: string[];
  /** Contract-format violations found while parsing (empty = clean). */
  problems: string[];
}

export interface GlossaryEntry {
  id: string;
  /** Display term from the `**<Display term>**` line under the heading. */
  display: string;
  /** Definition markdown (everything after the display line). */
  definition: string;
}

export interface Glossary {
  byId: Map<string, GlossaryEntry>;
  problems: string[];
}

const SECTION_ORDER = [
  'Purpose',
  'What you can do here',
  'Inputs & fields',
  'Terms on this page',
] as const;

/** Split `---` front-matter from the body. Returns null front-matter when absent. */
function splitFrontMatter(raw: string): { fm: string | null; body: string } {
  const lines = raw.split(/\r?\n/);
  if (lines[0]?.trim() !== '---') return { fm: null, body: raw };
  const end = lines.findIndex((l, i) => i > 0 && l.trim() === '---');
  if (end < 0) return { fm: null, body: raw };
  return { fm: lines.slice(1, end).join('\n'), body: lines.slice(end + 1).join('\n') };
}

/** Minimal `key: value` front-matter parse (the contract allows exactly 3 scalar keys). */
function parseFrontMatter(fm: string, problems: string[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const line of fm.split('\n')) {
    if (!line.trim()) continue;
    const m = /^([A-Za-z][A-Za-z0-9_-]*):\s*(.*)$/.exec(line);
    if (!m) {
      problems.push(`front-matter line is not \`key: value\`: "${line.trim()}"`);
      continue;
    }
    out.set(m[1], m[2].trim());
  }
  const allowed = new Set(['surfaceId', 'title', 'oneLiner']);
  for (const key of out.keys()) {
    if (!allowed.has(key)) problems.push(`front-matter key "${key}" is not in the contract (surfaceId | title | oneLiner)`);
  }
  return out;
}

/** Split the body into `## <heading>` sections, preserving order. */
function splitSections(body: string): { heading: string; content: string }[] {
  const sections: { heading: string; content: string }[] = [];
  let current: { heading: string; lines: string[] } | null = null;
  for (const line of body.split('\n')) {
    const m = /^##\s+(.+?)\s*$/.exec(line);
    if (m) {
      if (current) sections.push({ heading: current.heading, content: current.lines.join('\n').trim() });
      current = { heading: m[1], lines: [] };
    } else if (current) {
      current.lines.push(line);
    }
    // Text before the first heading is ignored (the contract has none).
  }
  if (current) sections.push({ heading: current.heading, content: current.lines.join('\n').trim() });
  return sections;
}

/**
 * Parse top-level bullet lines (`- …`); INDENTED lines fold into the current bullet
 * (soft-wrapped continuations); a non-indented non-bullet line ends it (prose paragraph — kept
 * in the section markdown, just not a bullet).
 */
function parseBullets(content: string): string[] {
  const bullets: string[] = [];
  let open = false;
  for (const line of content.split('\n')) {
    const m = /^-\s+(.*)$/.exec(line);
    if (m) {
      bullets.push(m[1].trim());
      open = true;
    } else if (/^\s+\S/.test(line) && open) {
      bullets[bullets.length - 1] += ` ${line.trim()}`;
    } else {
      open = false;
    }
  }
  return bullets;
}

/**
 * Extract the strict `- **<Field label>** — <body>` entries (em-dash or hyphen separator).
 * Non-matching bullets/prose are NOT violations — they render as markdown, they just aren't
 * InfoTip-addressable by field name.
 */
function parseFieldEntries(content: string): SurfaceFieldHelp[] {
  const fields: SurfaceFieldHelp[] = [];
  for (const bullet of parseBullets(content)) {
    const m = /^\*\*(.+?)\*\*\s*(?:—|–|-)\s*(.*)$/.exec(bullet);
    if (m) fields.push({ label: m[1].trim(), body: m[2].trim() });
  }
  return fields;
}

/** Parse one surface doc. Tolerant; violations land in `.problems`. */
export function parseSurfaceDoc(raw: string): SurfaceDoc {
  const problems: string[] = [];
  const { fm, body } = splitFrontMatter(raw);
  if (fm === null) problems.push('missing `---` front-matter block');
  const meta = fm !== null ? parseFrontMatter(fm, problems) : new Map<string, string>();
  for (const key of ['surfaceId', 'title', 'oneLiner'] as const) {
    if (!meta.get(key)) problems.push(`front-matter key "${key}" is missing or empty`);
  }

  const sections = splitSections(body);
  const headings = sections.map((s) => s.heading);
  for (const required of SECTION_ORDER) {
    if (!headings.includes(required)) problems.push(`missing section \`## ${required}\``);
  }
  const present = headings.filter((h) => (SECTION_ORDER as readonly string[]).includes(h));
  const inOrder = present.every((h, i) => i === 0 || SECTION_ORDER.indexOf(h as (typeof SECTION_ORDER)[number]) > SECTION_ORDER.indexOf(present[i - 1] as (typeof SECTION_ORDER)[number]));
  if (!inOrder) problems.push(`sections out of contract order (${SECTION_ORDER.join(' → ')})`);

  const byHeading = new Map(sections.map((s) => [s.heading, s.content]));
  const fieldsMd = byHeading.get('Inputs & fields') ?? '';

  const termIds = (byHeading.get('Terms on this page') ?? '')
    .split(/[,\n]/)
    .map((t) => t.trim())
    .filter(Boolean);
  for (const id of termIds) {
    if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(id)) problems.push(`term id "${id}" is not kebab-case`);
  }

  return {
    surfaceId: meta.get('surfaceId') ?? '',
    title: meta.get('title') ?? '',
    oneLiner: meta.get('oneLiner') ?? '',
    purpose: byHeading.get('Purpose') ?? '',
    actionsMd: byHeading.get('What you can do here') ?? '',
    fieldsMd,
    fields: parseFieldEntries(fieldsMd),
    extraSections: sections.filter((s) => !(SECTION_ORDER as readonly string[]).includes(s.heading)),
    termIds,
    problems,
  };
}

/**
 * Parse `glossary.md`: one `## <term-id>` per term; first non-empty line after the heading is
 * `**<Display term>**`; the rest is the definition (incl. the "Where you'll see it" line).
 */
export function parseGlossary(raw: string): Glossary {
  const problems: string[] = [];
  const byId = new Map<string, GlossaryEntry>();
  for (const section of splitSections(raw)) {
    const id = section.heading.trim();
    if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(id)) {
      problems.push(`glossary heading "${id}" is not a kebab-case term id`);
      continue;
    }
    if (byId.has(id)) {
      problems.push(`duplicate glossary term "${id}"`);
      continue;
    }
    const lines = section.content.split('\n');
    const firstIdx = lines.findIndex((l) => l.trim());
    const first = firstIdx >= 0 ? lines[firstIdx].trim() : '';
    const dm = /^\*\*(.+?)\*\*$/.exec(first);
    if (!dm) {
      problems.push(`glossary term "${id}" is missing its \`**<Display term>**\` first line`);
    }
    const display = dm ? dm[1].trim() : id;
    const definition = (firstIdx >= 0 ? lines.slice(firstIdx + 1) : lines).join('\n').trim();
    byId.set(id, { id, display, definition });
  }
  return { byId, problems };
}
